import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Sparkles, Send, ShieldCheck, Loader2, Mail, MessageSquare, RefreshCw, CheckCircle2 } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function AdminInbox() {
  const { user, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState("");
  const [subject, setSubject] = useState("");
  const [tone, setTone] = useState("warm");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);

  const load = async () => {
    try {
      const [s, m] = await Promise.all([
        api.get("/api/admin/stats"),
        api.get("/api/admin/messages"),
      ]);
      setStats(s.data);
      setMessages(m.data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(e.response?.data?.detail || "Load failed");
    }
  };

  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  const bootstrap = async () => {
    setBootstrapping(true);
    try {
      await api.post("/api/admin/bootstrap");
      await refresh();
      toast.success("You are now an admin.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bootstrap failed");
    } finally { setBootstrapping(false); }
  };

  const pick = (msg) => {
    setSelected(msg);
    setDraft("");
    setSubject(`Re: ${msg.subject}`);
  };

  const generateDraft = async () => {
    if (!selected) return;
    setDrafting(true);
    try {
      const { data } = await api.post("/api/admin/draft-reply", {
        email: selected.email, created_at: selected.created_at, tone,
      });
      setDraft(data.draft);
      setSubject(data.suggested_subject);
      toast.success("Draft ready — review and send.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft failed");
    } finally { setDrafting(false); }
  };

  const sendReply = async () => {
    if (!selected || !draft.trim()) return;
    setSending(true);
    try {
      await api.post("/api/admin/send-reply", {
        email: selected.email,
        created_at: selected.created_at,
        subject,
        body_markdown: draft,
      });
      toast.success("Reply sent");
      setDraft("");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  if (!user) return null;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-lg mx-auto px-5 lg:px-8 py-24 text-center">
          <div className="h-14 w-14 rounded-full bg-neutral-100 flex items-center justify-center mx-auto"><ShieldCheck size={22} /></div>
          <h1 className="font-display text-3xl font-bold mt-6">Admin access required</h1>
          <p className="text-neutral-600 mt-2">The first user to bootstrap becomes admin. If you're the founder, claim it now.</p>
          <button onClick={bootstrap} disabled={bootstrapping} className="btn-primary mt-6" data-testid="admin-bootstrap">
            {bootstrapping && <Loader2 size={16} className="animate-spin" />} Claim admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="overline mb-2">ADMIN · INBOX</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight" data-testid="admin-title">Reply with AI. Ship in seconds.</h1>
          </div>
          <button onClick={load} className="btn-ghost text-sm"><RefreshCw size={14} /> Refresh</button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-10" data-testid="admin-stats">
            <StatMini label="Messages" v={stats.total_messages} />
            <StatMini label="Unreplied" v={stats.unreplied_messages} highlight />
            <StatMini label="Users" v={stats.total_users} />
            <StatMini label="Subscribers" v={stats.active_subscribers} />
            <StatMini label="Docs" v={stats.documents_generated} />
            <StatMini label="Applications" v={stats.applications_tracked} />
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Messages list */}
          <div className="lg:col-span-2 gf-card p-0 overflow-hidden">
            <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
              <div className="font-medium flex items-center gap-2"><Mail size={16} /> Inbox</div>
              <Badge variant="secondary">{messages.length}</Badge>
            </div>
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-neutral-200">
              {messages.length === 0 && <div className="p-6 text-sm text-neutral-500 text-center">No messages yet. Test by submitting the contact form.</div>}
              {messages.map((m) => {
                const isSel = selected?.email === m.email && selected?.created_at === m.created_at;
                return (
                  <button key={m.created_at + m.email} onClick={() => pick(m)}
                    className={`w-full text-left p-4 transition-colors ${isSel ? "bg-neutral-50" : "hover:bg-neutral-50"}`}
                    data-testid={`admin-msg-${m.email}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-medium truncate">{m.name}</span>
                      {m.replied_at ? (
                        <span className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full flex items-center gap-1"><CheckCircle2 size={10} /> Sent</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">New</span>
                      )}
                    </div>
                    <div className="text-xs text-neutral-500 truncate">{m.email} · {new Date(m.created_at).toLocaleString()}</div>
                    <div className="text-sm font-medium mt-1 truncate">{m.subject}</div>
                    <div className="text-xs text-neutral-600 mt-1 line-clamp-2">{m.message}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Reply panel */}
          <div className="lg:col-span-3">
            {!selected ? (
              <div className="gf-card p-16 text-center text-neutral-500">
                Pick a message on the left to draft a reply with AI.
              </div>
            ) : (
              <div className="gf-card p-6" data-testid="admin-reply-panel">
                <div className="mb-4">
                  <div className="text-xs uppercase tracking-widest text-neutral-500">From</div>
                  <div className="font-medium">{selected.name} &lt;{selected.email}&gt;</div>
                  {selected.org_name && <div className="text-xs text-neutral-500 mt-0.5">Org: {selected.org_name}</div>}
                </div>
                <div className="border border-neutral-200 rounded-lg p-4 bg-neutral-50 whitespace-pre-wrap text-sm mb-6">
                  <div className="font-medium mb-2">{selected.subject}</div>
                  {selected.message}
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger className="w-40 h-10" data-testid="tone-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="warm">Warm</SelectItem>
                      <SelectItem value="formal">Formal</SelectItem>
                      <SelectItem value="brief">Brief</SelectItem>
                    </SelectContent>
                  </Select>
                  <button onClick={generateDraft} disabled={drafting} className="btn-accent text-sm" data-testid="admin-draft-btn">
                    {drafting ? <><Loader2 size={14} className="animate-spin" /> Drafting…</> : <><Sparkles size={14} /> AI draft reply</>}
                  </button>
                </div>

                <div className="mb-3">
                  <label className="text-xs uppercase tracking-widest text-neutral-500">Subject</label>
                  <input value={subject} onChange={(e) => setSubject(e.target.value)}
                    className="mt-1 w-full h-10 border border-neutral-200 rounded-lg px-3" data-testid="reply-subject" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500">Reply body</label>
                  <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={10}
                    placeholder="Click 'AI draft reply' — or type manually."
                    className="mt-1 w-full border border-neutral-200 rounded-lg p-3 font-mono text-sm"
                    data-testid="reply-body" />
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <button onClick={sendReply} disabled={sending || !draft.trim()} className="btn-primary" data-testid="admin-send-btn">
                    {sending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send reply</>}
                  </button>
                  <button onClick={() => { setSelected(null); setDraft(""); }} className="btn-ghost text-sm">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatMini({ label, v, highlight }) {
  return (
    <div className={`border rounded-lg p-4 ${highlight ? "border-amber-300 bg-amber-50" : "border-neutral-200"}`}>
      <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-display text-2xl font-bold mt-1">{v}</div>
    </div>
  );
}
